import cn from 'classnames';
import { Icon } from '@nespresso-design-system/components';
import useTranslations from '@/widgets/hooks/useTranslations';
import styles from './PricingDetails.module.scss';
import type { Product } from '@/domain/Product';
import type { Options, Translations } from '@/widgets/ProductList/config';
import type { PricingModel } from '@/domain/Pricing';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions';

export type PricingDetailsProps = {
  pricingModel: PricingModel;
  product: Pick<
    Product,
    | 'pricing'
    | 'salesMultiple'
    | 'technologies'
    | 'unitQuantity'
    | 'bundle'
    | 'type'
  >;
  class?: string;
};

export default function PricingDetails({
  pricingModel,
  product,
  class: className
}: PricingDetailsProps) {
  const { pricing, salesMultiple, unitQuantity, bundle, technologies } =
    product;
  const options = useWidgetOptions<Options>();
  const { t } = useTranslations<Translations>();
  const originalLabel = t('productList.cards.capsuleLabel.originalText');
  const vertuoLabel = t('productList.cards.capsuleLabel.vertuoText');
  const shouldShowPricingDetails = product.type === 'capsule';
  const isTechnologyOriginal = technologies[0] === 'original';
  const totalNumOfCapsules = salesMultiple * unitQuantity;
  const iconType = isTechnologyOriginal
    ? '24/coffee/capsule-ol'
    : '24/coffee/capsule-carafe-vl';
  const title = isTechnologyOriginal ? originalLabel : vertuoLabel;

  /* if you look at this line seems incorrect, but it is correct
  because we want to show the other price characteristic next
  for example, if pricingModel is 'SLEEVE', we want to show pricePerCapsule
  and if pricingModel is 'CAPSULE', we want to show pricePerSleeve */
  const pricePer =
    pricingModel === 'SLEEVE'
      ? pricing.pricePerCapsule
      : pricing.pricePerSleeve;

  const displayPricePerWeight =
    options.displayPricePerWeight &&
    shouldShowPricingDetails &&
    product.pricing.pricePerWeight !== undefined &&
    !bundle;

  const displayDefaultPricing =
    !displayPricePerWeight && pricePer && !bundle && shouldShowPricingDetails;

  const displayPrice = displayPricePerWeight || displayDefaultPricing;

  return (
    <div className={cn(styles.container, className)}>
      {displayPrice && (
        <div className={styles.pricingSection}>
          {t(
            `productList.cards.purchase.${displayPricePerWeight ? 'pricePerWeight' : 'pricePerLabel'}`,
            {
              price: displayPricePerWeight
                ? product.pricing.pricePerWeight
                : pricePer,
              formatParams: {
                price: {
                  withoutTrailingZeros: true
                }
              }
            }
          )}{' '}
          <span className={styles.totalCapsulesSection}>
            ({totalNumOfCapsules}
            <Icon
              icon={iconType}
              title={title}
              className={styles.technologyIcon}
              method="image"
            />
            )
          </span>
        </div>
      )}
      {bundle && (
        <div className={styles.perCapsule}>
          {totalNumOfCapsules} {t('productList.cards.purchase.capsulesText')}
        </div>
      )}
    </div>
  );
}
